import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { paginatePrintPages, paginateDirectPrint } from '../../utils/paginatePrintPages';

// Format a date as DD/MM/YYYY.
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Format a number to 2 decimals.
const formatNumber = (num) => (Number(num) || 0).toFixed(2);

// Calculate total TTC for an item (same as UI calculation)
const calculateTotalTTC = (qte, pvTTC, remise) => {
  const discountedPrice = (pvTTC * (1 - remise / 100));
  const total = qte * discountedPrice;
  // Round to 2 decimal places like Excel's ROUND function
  return Math.round(total * 100) / 100;
};

const paginationOptions = {
  tableSelector: '.bonsortie-doc-table',
  repeatNotes: true,
  showReporter: false,
};

// Page template for bon de sortie - with an isolation class "bonsortie-doc"
const pageTemplate = `
<div class="bonsortie-doc page">
  <table class="bonsortie-doc-table">
    <thead>
      <tr>
        <td colspan="6" class="header-content">
          <div class="header-line">
            <div>
              <div>Bon Sortie N°: {{REF_BS}}</div>
              <div>{{ENTREPRISE_NAME}} {{ENTREPRISE_LOCATION}} {{COMPTE}}</div>
            </div>
            <div>
              <div>Date: {{DATE_BS}}</div>
              <div>Page: <span data-page-number>{{CURRENT_PAGE}} / {{TOTAL_PAGES}}</span></div>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <th class="right qte-col" style="width: 3%">Qté</th>
        <th class="left designation-col" style="width: 50%">Désignation</th>
        <th class="left" style="width: 15%">Réf</th>
        <th class="right prix-col" style="width: 10%">Prix U</th>
        <th class="left remise-col" style="width: 7%">R %</th>
        <th class="right" style="width: 10%">Total</th>
      </tr>
    </thead>
    <tbody>
      {{ITEMS_ROWS}}
    </tbody>
    {{FOOTER}}
  </table>
  <div class="footer-note">
                    Les marchandises voyagent aux risques & périls du destinataire. dépassé 15 jours la marchandise n'est ni reprise ni échangée.
                </div>
</div>`;

// Main document template - with scoped CSS selectors
const bonSortieTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Bon de Sortie - {{REF_BS}}</title>
  <style>
    /* Isolate styles with specific selectors */
    @media print {
      .bonsortie-doc.page { 
        page-break-after: always;
        padding-top: 30mm !important;  /* Force top padding */
        box-sizing: border-box;
      }
      .bonsortie-doc.page:last-child { page-break-after: auto; }
      .bonsortie-doc tr { break-inside: avoid; }
    }
    @page { size: A4; margin: 1cm 1cm 1.1cm 1cm; }
    
    /* Scoped styles to prevent leakage */
    .bonsortie-doc * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: Arial, sans-serif;
      color: #000;
      margin: 0;
      padding: 5px;
    }
    .bonsortie-doc-table {
      width: 95%;
      margin: 0 auto;
      border-collapse: collapse;
      font-size: 11px;
    }
    .bonsortie-doc .header-content {
      padding: 10px 5px; 
      border: none;
    }
    .bonsortie-doc .header-line {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    /* Completely borderless table with clear alignments */
    .bonsortie-doc th {
      border: none;
      padding: 5px 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
      background: #f0f0f0;
      font-size: 10px;
      font-weight: bold;
      text-transform: uppercase;
    }
    .bonsortie-doc td {
      border: none;
      padding: 4px 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
    }
    /* Additional spacing between specific columns */
    .bonsortie-doc .qte-col {
      padding-right: 15px;
    }
    .bonsortie-doc .designation-col {
      padding-left: 15px;
    }
    .bonsortie-doc .prix-col {
      padding-right: 15px;
    }
    .bonsortie-doc .remise-col {
      padding-left: 15px;
    }
    /* Row styling */
    /* Header row with bottom border only for visual separation */
    .bonsortie-doc thead tr:last-child {
      border-bottom: 2px solid #000;
    }
    /* Subtle separators between data rows */
    .bonsortie-doc .item-row {
      border-bottom: 1px solid #f0f0f0;
    }
    /* Customized alignment for different columns */
    .bonsortie-doc .right {
      text-align: right;
      max-width: 60px;
    }
    .bonsortie-doc .left {
      text-align: left;
    }
    .bonsortie-doc .total-section {
      text-align: right;
      font-weight: bold;
      border-top: 2px solid #000;
      padding: 10px 6px 6px;
      font-size: 11px;
      margin-top: 5px;
    }

    .footer-note {
            margin-top: 5px;
            font-size: 9px;
            font-style: italic;
        }

  </style>
</head>
<body>
  {{PAGES}}
</body>
</html>`;

// Helper to process templates - replace placeholders with actual values
const processTemplate = (template, replacements) => {
  let processedTemplate = template;
  Object.entries(replacements).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    processedTemplate = processedTemplate.replace(regex, value || '');
  });
  return processedTemplate;
};

// Generate pages content with scoped class names
const generatePages = (bonliv, items) => {
  // Sort items by CODE_ART in ascending order
  const sortedItems = [...items].sort((a, b) => 
    (a.CODE_ART || '').localeCompare(b.CODE_ART || '')
  );
  
  // Build one source page; paginate after the output's print styles are applied.
  let rowsHtml = '';

  sortedItems.forEach((item) => {
    const calculatedTotal = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);
    rowsHtml += `
        <tr class="item-row">
          <td class="right qte-col">${item.QTE}</td>
          <td class="left designation-col">${item.ARTICLE || ''}</td>
          <td class="left">${item.article?.REF_OEM || ''}</td>
          <td class="right prix-col">${formatNumber(item.PV_TTC)}</td>
          <td class="left remise-col">${formatNumber(item.REMISE)}</td>
          <td class="right">${formatNumber(calculatedTotal)}</td>
        </tr>`;
  });

  // The paginator keeps this total on the last page only.
  const footer = `<tfoot>
    <tr>
      <td colspan="6" class="total-section">Total: ${formatNumber(bonliv.MNT_TTC)} DH</td>
    </tr>
  </tfoot>`;

  return processTemplate(pageTemplate, {
    REF_BS: bonliv.REF_FAC ? bonliv.REF_FAC.slice(2) : '',
    ENTREPRISE_NAME: bonliv.CLIENT || '',
    ENTREPRISE_LOCATION: bonliv.client?.VILLE || '',
    COMPTE: bonliv.client?.COMPTE || '',
    DATE_BS: formatDate(bonliv.DATE_FAC),
    CURRENT_PAGE: 1,
    TOTAL_PAGES: 1,
    ITEMS_ROWS: rowsHtml,
    FOOTER: footer
  });
};

// PDF generation helper function with isolation
const generatePdfBlob = async (htmlString) => {
  // Create a container completely isolated from the main document
  const container = document.createElement('div');
  container.style.width = '210mm';
  container.style.position = 'absolute';
  container.style.left = '-10000px';
  container.innerHTML = htmlString;
  document.body.appendChild(container);
  
  try {
    const sourcePage = container.querySelector('.page');
    const margin = 10; // 10mm for sides
    sourcePage.style.width = '210mm';
    sourcePage.style.height = '297mm';
    sourcePage.style.padding = `20mm ${margin}mm 11mm ${margin}mm`;
    sourcePage.style.boxSizing = 'border-box';
    sourcePage.style.backgroundColor = '#FFFFFF';
    const pages = await paginatePrintPages(container, { ...paginationOptions, pageHeightMm: 297 });
    const pdf = new jsPDF('p', 'mm', 'a4');

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      
      const canvas = await html2canvas(page, {
          scale: 3,
          useCORS: true,
          logging: false,
          letterRendering: true,
          backgroundColor: '#FFFFFF',
          width: page.offsetWidth,
          height: page.offsetHeight,
          fontFamily: 'Arial',
      });
      // Add image to PDF
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297); // Direct A4 dimensions
    }

    return pdf.output('blob');
  } finally {
    document.body.removeChild(container);
  }
};

// Main export function
export const printBonSortie = async (bonliv, items) => {
  try {
    // Create loading modal with "Sortir" button
    const loadingModalDiv = document.createElement('div');
    document.body.appendChild(loadingModalDiv);
    
    const closeLoadingModal = () => {
      ReactDOM.unmountComponentAtNode(loadingModalDiv);
      document.body.removeChild(loadingModalDiv);
    };
    
    ReactDOM.render(
      <Modal
        title="Generating PDF"
        open={true}
        closable={true}
        maskClosable={false}
        footer={[
          <Button key="exit" onClick={closeLoadingModal}>
            Sortir
          </Button>
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spin 
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            tip="Preparing document..."
          />
        </div>
      </Modal>,
      loadingModalDiv
    );

    // Generate pages content and full HTML
    const pagesContent = generatePages(bonliv, items);
    const html = processTemplate(bonSortieTemplate, {
      REF_BS: bonliv.REF_BL || '',
      PAGES: pagesContent,
    });

    // Generate the PDF blob
    const pdfBlob = await generatePdfBlob(html);
    
    // Close loading modal
    closeLoadingModal();

    // Create URL for the blob
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    // Create preview modal with "Sortir" button
    const previewModalDiv = document.createElement('div');
    document.body.appendChild(previewModalDiv);
    
    const closePreviewModal = () => {
      URL.revokeObjectURL(pdfUrl);
      ReactDOM.unmountComponentAtNode(previewModalDiv);
      document.body.removeChild(previewModalDiv);
    };
    
    ReactDOM.render(
      <Modal
        title={`PDF Preview - Bon de Sortie ${bonliv.REF_BL || ''}`}
        open={true}
        width="90%"
        centered={true}
        closable={true}
        maskClosable={true}
        className="pdf-preview-modal"
        style={{ top: 20, maxWidth: '1400px' }}
        footer={[
          <Button key="exit" onClick={closePreviewModal}>
            Sortir
          </Button>
        ]}
        onCancel={closePreviewModal}
      >
        <div className="pdf-preview-container">
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '80vh', border: 'none' }}
            title="PDF Preview"
          />
        </div>
      </Modal>,
      previewModalDiv
    );

    return {
      cleanup: closePreviewModal,
      pdfUrl
    };
  } catch (error) {
    console.error('Error generating bon de sortie:', error);
    Modal.error({
      title: 'Error',
      content: `Failed to generate document: ${error.message}`,
    });
    throw new Error(`Failed to generate document: ${error.message}`);
  }
};

export const printBonSortieDirect = async (bonliv, items, commercial = null) => {
  try {
      // Generate pages content and full HTML (using bon sortie template)
      const pagesContent = generatePages(bonliv, items);
      const html = processTemplate(bonSortieTemplate, {  // Use appropriate template
          REF_BS: bonliv.REF_FAC ? bonliv.REF_FAC.slice(2) : '',
          PAGES: pagesContent,
      });

      // Create an invisible iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position: absolute; width: 190mm; height: 297mm; left: -9999px; border: 0;';
      document.body.appendChild(iframe);
      
      // Write the HTML content to the iframe
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      // Set up cleanup after printing
      iframe.onload = () => {
          setTimeout(async () => {
              try {
                  await paginateDirectPrint(doc, 276, paginationOptions);
                  iframe.contentWindow.focus();
                  iframe.contentWindow.print();
                  
                  // Remove the iframe after printing
                  setTimeout(() => {
                      document.body.removeChild(iframe);
                  }, 1000);
              } catch (err) {
                  console.error('Error in print process:', err);
                  document.body.removeChild(iframe);
              }
          }, 500);
      };
      doc.open();
      doc.write(html);
      doc.close();
  } catch (error) {
      console.error('Error generating bon de sortie for direct print:', error);
      throw new Error(`Failed to generate document: ${error.message}`);
  }
};
